import type { SearchKeywordWithSlug } from '@/features/searchV2/types/SearchKeywords.types';
import type { TranslationMap } from '@/locale/translation-map';

export type SerpSeoEntry = {
  slugs: SearchKeywordWithSlug['slug'][];
  descriptionKey: keyof TranslationMap;
  ctaKey: keyof TranslationMap;
};

/**
 * Mapping between indexable search keyword slugs and their SEO description/CTA translation keys.
 * Key resolution: 'default' uses descriptionKey as-is; other types append `.${type}` at runtime.
 * All type-variant keys (e.g. seo.haircutDescription.sistaminuten) must exist in sv.json.
 */
export const serpSeo: SerpSeoEntry[] = [
  {
    slugs: [
      'klippning',
      'harforlangning',
      'barberare',
      'keratin',
      'frisor',
      'damklippning',
      'herrklippning',
      'barnklippning',
      'pensionarklippning',
      'klippning-ungdom',
      'luggklippning',
      'klippning-slingor',
      'balayage',
      'babylights',
      'blowout',
      'bottenfarg',
      'color-correction',
      'extensions',
      'extensions-borttagning',
      'face-framing',
      'fargning',
      'foning',
      'keratinbehandling',
      'ny-frisyr',
      'olaplex',
      'olaplexbehandling',
      'ombre',
      'rakning',
      'slingor',
      'stylist',
      'toning',
      'tvatt-fon',
      'skagg',
      'skaggfargning',
      'skaggklippning',
      'skaggtrimmning',
      'brollopsuppsattning',
      'k18',
      'barber',
      'permanent',
      'skaggvard',
    ],
    descriptionKey: 'seo.haircutDescription',
    ctaKey: 'seo.haircutTitleCta',
  },
  {
    slugs: ['harvard', 'harbottenbehandling', 'fet-harbotten', 'torr-harbotten'],
    descriptionKey: 'seo.hairCareDescription',
    ctaKey: 'seo.hairCareTitleCta',
  },
  {
    slugs: [
      'harborttagning',
      'ipl-harborttagning',
      'permanent-harborttagning',
      'vaxning',
      'vaxning-brasiliansk',
      'sockring',
      'sugaring',
    ],
    descriptionKey: 'seo.hairRemovalDescription',
    ctaKey: 'seo.hairRemovalTitleCta',
  },
  {
    slugs: [
      'massage',
      'thaimassage',
      'svensk-massage',
      'klassisk-massage',
      'lymfmassage',
      'gravidmassage',
      'fotmassage',
      'ansiktsmassage',
      'taktil-massage',
      'koppningsmassage',
      'idrottsmassage',
      'aromamassage',
      'ayurvedisk-massage',
      'bambumassage',
      'faceliftmassage',
      'fibromassage',
      'friskvardsmassage',
      'gua-sha-massage',
      'hot-stone-massage',
      'ir-massage',
      'japansk-massage',
      'klangmassage',
      'lpg-massage',
      'medicinsk-massage',
      'nbe-massage',
      'regndroppsmassage',
      'reiki-massage',
      'ryggmassage',
      'skalpmassage',
      'shiatsu',
      'triggerpunktsmassage',
      'tuina-massage',
      'vibrationsmassage',
      'yogamassage',
      'andningsmassage',
    ],
    descriptionKey: 'seo.massageDescription',
    ctaKey: 'seo.massageTitleCta',
  },
  {
    slugs: [
      'naglar',
      'pedikyr',
      'nagelforlangning',
      'manikyr',
      'nagelvard',
      'gellack',
      'gele-naglar',
      'akrylnaglar',
      'fransk-manikyr',
      'biab',
      'gel-naglar',
      'gelenaglar',
      'gellack-med-forstarkning',
      'manikyr-pedikyr',
      'nagelforlangning-akryl',
      'nagelforlangning-gele',
      'nagelforlangning-glasfiber',
      'nagelforlangning-silke',
      'nagelforstarkning',
      'nagelklippning',
      'naglar-borttagning',
      'naglar-reparation',
      'ombre-naglar',
      'paraffinbehandling',
      'spa-manikyr-pedikyr',
      'spa-manikyr',
      'spa-pedikyr',
    ],
    descriptionKey: 'seo.nailsDescription',
    ctaKey: 'seo.nailsTitleCta',
  },
  {
    slugs: [
      'fransar',
      'fransforlangning',
      'lashlift',
      'ogonbryn',
      'browlift',
      'microblading',
      'tradning',
      'singel-fransar',
      'fransfargning',
      'volymfransar',
      'fransborttagning',
      'fransforlangning-megavolym',
      'fransforlangning-volym',
      'fransrengoring',
      'katun-fransar',
      'lashlift-keratin',
      'megavolymfransar',
      'mixfransar',
      'ombre-brows',
      'powder-brows',
      'singelfransar',
      'ogonfransar',
      'ogonfransforlangning',
      'eyeliner-tatuering',
      'brynformning',
      'brynfargning',
      'brynplockning',
      'ogonbrynsfarg',
      'ogonbrynstatuering',
      'permanent-ogonbrynsmakeup',
      'microshading',
      'lash-lift',
    ],
    descriptionKey: 'seo.lashesDescription',
    ctaKey: 'seo.lashesTitleCta',
  },
  {
    slugs: ['akupunktur'],
    descriptionKey: 'seo.acupunctureDescription',
    ctaKey: 'seo.acupunctureTitleCta',
  },
  {
    slugs: [
      'ansiktsbehandling',
      'microneedling',
      'lpg',
      'hifu',
      'dermapen',
      'pigmentflackar',
      'rosaceabehandling',
      'karlbehandling',
      'aknebehandling',
      'hudvard',
      'ansiktslyft-utan-kirurgi',
      'ansiktsbehandling-djuprengorande',
      'celluliter',
      'cryoterapi',
      'diamantslipning',
      'enzympeeling',
      'fettreducering',
      'hollywood-peel',
      'hudforyngring',
      'huduppstramning',
      'hyaluronsyra',
      'ipl',
      'kemisk-peeling',
      'kroppsinpackning',
      'kroppspeeling',
      'laserbehandling',
      'led-ljusterapi',
      'lpg-behandling',
      'melasma',
      'mesoterapi',
      'micropen',
      'picolaser',
      'pigmentbehandling',
      'prx-t33',
      'psoriasis',
      'radiofrekvens',
      'rosacea',
      'sklerosering',
      'svettbehandling',
      'torrborstning',
      'vampyrbehandling',
      'akne',
      'fettfrysning',
    ],
    descriptionKey: 'seo.facialsDescription',
    ctaKey: 'seo.facialsTitleCta',
  },
  {
    slugs: [
      'fillers',
      'prp',
      'prp-platelet-rich-plasma',
      'hyperhidros',
      'plastikkirurgi',
      'skinbooster',
      'injektionsbehandlingar',
      'botox',
    ],
    descriptionKey: 'seo.fillersDescription',
    ctaKey: 'seo.fillersTitleCta',
  },
  {
    slugs: ['skonhet', 'makeup', 'spraytan', 'personal-shopper'],
    descriptionKey: 'seo.beautyDescription',
    ctaKey: 'seo.beautyTitleCta',
  },
  {
    slugs: ['tatuering', 'kosmetisk-tatuering', 'tatueringsborttagning', 'piercing', 'navelpiercing', 'lapptatuering'],
    descriptionKey: 'seo.tattooDescription',
    ctaKey: 'seo.tattooTitleCta',
  },
  {
    slugs: [
      'fotvard',
      'medicinsk-fotvard',
      'estetisk-fotvard',
      'fotsvamp',
      'halsprickor',
      'liktornar',
      'nagelsvamp',
      'nageltrang',
      'skoinlagg',
      'vartor',
    ],
    descriptionKey: 'seo.footCareDescription',
    ctaKey: 'seo.footCareTitleCta',
  },
  {
    slugs: [
      'yoga',
      'gravidyoga',
      'ashtanga-yoga',
      'hatha-yoga',
      'hot-yoga',
      'power-yoga',
      'restorative-yoga',
      'senioryoga',
      'vinyasa-yoga',
      'yin-yoga',
      'yoga-nidra',
      'qigong',
    ],
    descriptionKey: 'seo.yogaDescription',
    ctaKey: 'seo.yogaTitleCta',
  },
  {
    slugs: ['traning', 'grupptraning', 'sjukgymnastik', 'sport'],
    descriptionKey: 'seo.fitnessDescription',
    ctaKey: 'seo.fitnessTitleCta',
  },
  {
    slugs: ['pt', 'personlig-tranare', 'personlig-traning'],
    descriptionKey: 'seo.ptDescription',
    ctaKey: 'seo.ptTitleCta',
  },
  {
    slugs: ['zumba'],
    descriptionKey: 'seo.zumbaDescription',
    ctaKey: 'seo.zumbaTitleCta',
  },

  {
    slugs: ['friskvard', 'halsa'],
    descriptionKey: 'seo.wellnessDescription',
    ctaKey: 'seo.wellnessTitleCta',
  },
  {
    slugs: ['healing'],
    descriptionKey: 'seo.healingDescription',
    ctaKey: 'seo.healingTitleCta',
  },
  {
    slugs: ['osteopati'],
    descriptionKey: 'seo.osteopathyDescription',
    ctaKey: 'seo.osteopathyTitleCta',
  },
  {
    slugs: [
      'samtalsterapi',
      'terapi',
      'zonterapi',
      'frekvensterapi',
      'funktionsanalys',
      'kakledsbehandling',
      'radgivning',
      'konsultation',
    ],
    descriptionKey: 'seo.therapyDescription',
    ctaKey: 'seo.therapyTitleCta',
  },
  {
    slugs: ['naprapati'],
    descriptionKey: 'seo.naprapatDescription',
    ctaKey: 'seo.naprapatTitleCta',
  },
  {
    slugs: ['kiropraktik'],
    descriptionKey: 'seo.chiropracticDescription',
    ctaKey: 'seo.chiropracticTitleCta',
  },
  {
    slugs: ['coachning', 'ledarskap'],
    descriptionKey: 'seo.coachingDescription',
    ctaKey: 'seo.coachingTitleCta',
  },
  {
    slugs: ['spa', 'headspa'],
    descriptionKey: 'seo.spaDescription',
    ctaKey: 'seo.spaTitleCta',
  },
  {
    slugs: ['tandblekning'],
    descriptionKey: 'seo.dentalWhiteningDescription',
    ctaKey: 'seo.dentalWhiteningTitleCta',
  },
  {
    slugs: ['tandlakare', 'tandvard'],
    descriptionKey: 'seo.dentalDescription',
    ctaKey: 'seo.dentalTitleCta',
  },
  {
    slugs: ['vaccination'],
    descriptionKey: 'seo.vaccinationDescription',
    ctaKey: 'seo.vaccinationTitleCta',
  },
  {
    slugs: ['veterinar', 'veterinarer'],
    descriptionKey: 'seo.vetDescription',
    ctaKey: 'seo.vetTitleCta',
  },
  {
    slugs: ['optiker'],
    descriptionKey: 'seo.opticianDescription',
    ctaKey: 'seo.opticianTitleCta',
  },
  {
    slugs: ['ortopedi'],
    descriptionKey: 'seo.orthopedicsDescription',
    ctaKey: 'seo.orthopedicsTitleCta',
  },
  {
    slugs: ['psykolog'],
    descriptionKey: 'seo.psychologistDescription',
    ctaKey: 'seo.psychologistTitleCta',
  },
  {
    slugs: ['lakare'],
    descriptionKey: 'seo.doctorDescription',
    ctaKey: 'seo.doctorTitleCta',
  },
  {
    slugs: ['fotografering'],
    descriptionKey: 'seo.photographyDescription',
    ctaKey: 'seo.photographyTitleCta',
  },
  {
    slugs: ['djurvard', 'hund', 'katt', 'hundfrisor', 'hast'],
    descriptionKey: 'seo.petCareDescription',
    ctaKey: 'seo.petCareTitleCta',
  },
  {
    slugs: ['naringsterapi', 'bioresonansterapi', 'kraniosakral-terapi', 'kroppsterapeuter', 'naturmedicin'],
    descriptionKey: 'seo.bodyTherapyDescription',
    ctaKey: 'seo.bodyTherapyTitleCta',
  },
  {
    slugs: ['kurser', 'utbildningar', 'utbildning'],
    descriptionKey: 'seo.coursesDescription',
    ctaKey: 'seo.coursesTitleCta',
  },
  {
    slugs: ['stadning', 'stadservice'],
    descriptionKey: 'seo.cleaningDescription',
    ctaKey: 'seo.cleaningTitleCta',
  },
  {
    slugs: ['dackbyte', 'dackhotell', 'bilvard', 'biltvatt', 'dackverkstad'],
    descriptionKey: 'seo.tireDescription',
    ctaKey: 'seo.tireTitleCta',
  },
];
